import { b as federationFragmentManifest } from "../common/consts.js";
import { j as joinPath } from "../common/helpers.js";
import { g as getFragmentUrl } from "./helpers.js";
function useFragmentNetwork(options) {
  const { fragmentName, versions, processException } = options;
  const fragmentVersion = versions.getVersion(fragmentName);
  const url = getFragmentUrl(fragmentName, fragmentVersion);
  const manifestUrl = joinPath(url, federationFragmentManifest);
  const preloadMap = {
    localization: /* @__PURE__ */ new Map(),
    styles: /* @__PURE__ */ new Map(),
    modules: /* @__PURE__ */ new Map()
  };
  const insertMap = {
    styles: /* @__PURE__ */ new Map(),
    modules: /* @__PURE__ */ new Map()
  };
  let manifest;
  let manifestPromise;
  return {
    url,
    importModule,
    insertStyles,
    loadLocalizationResources,
    getManifest,
    loadManifest,
    preload,
    getName,
    getVersion
  };
  function getName() {
    return manifest == null ? void 0 : manifest.name;
  }
  function getVersion() {
    return manifest == null ? void 0 : manifest.version;
  }
  function getEntryFile(entryName) {
    return `${entryName}.js`;
  }
  async function importModule(entryName) {
    const dep = getEntryFile(entryName);
    const entryUrl = joinPath(url, dep);
    const insertModulePromise = insertMap.modules.get(dep);
    if (insertModulePromise) {
      return insertModulePromise;
    }
    const promise = import(
      /* @vite-ignore */
      /* webpackIgnore: true */
      entryUrl
    ).then((module) => module.default).catch((error) => {
      insertMap.modules.delete(dep);
      throw error;
    });
    insertMap.modules.set(dep, promise);
    return promise;
  }
  async function loadLocalizationResources(languages) {
    await loadManifest();
    if (!manifest.translations) {
      return;
    }
    const language = languages.find((item) => manifest.translations[item]);
    if (!language) {
      return;
    }
    const resources = await fetch(joinPath(url, manifest.translations[language])).then(
      (res) => res.json()
    );
    return {
      resources,
      language
    };
  }
  async function insertStyles(entryName) {
    await loadManifest();
    const entryStatic = manifest.entryStatic[entryName];
    const promises = [...entryStatic.css].map(insertStylesDedupe);
    await Promise.all(promises).catch(catchLinksErrors.bind(null, entryName));
  }
  function catchLinksErrors(entryName, error) {
    processException(error, { url, fragmentName, fragmentVersion, entryName });
  }
  async function preload({ entryName, language }) {
    const promises = [preloadEntry(entryName)];
    await loadManifest();
    const entryStatic = manifest.entryStatic[entryName];
    promises.push(
      ...[...entryStatic.imports].map(preloadModuleDedupe),
      ...[...entryStatic.css].map(preloadStylesDedupe)
    );
    if (language) {
      promises.push(preloadLocalization(language));
    }
    await Promise.all(promises).catch(catchLinksErrors.bind(null, entryName));
  }
  async function preloadLocalization(language) {
    var _a;
    const dep = (_a = manifest.translations) == null ? void 0 : _a[language];
    if (!dep) {
      return;
    }
    if (!preloadMap.localization.has(language)) {
      const promise = appendLink(dep, {
        rel: "preload",
        as: "fetch",
        crossorigin: true
      }).catch((error) => {
        preloadMap.localization.delete(language);
        throw error;
      });
      preloadMap.localization.set(language, promise);
    }
    await preloadMap.localization.get(language);
  }
  function preloadEntry(entryName) {
    let mainEntryUrl = getEntryFile(entryName);
    if (process.env.NODE_ENV === "development") {
      mainEntryUrl += "?import";
    }
    return preloadModuleDedupe(mainEntryUrl);
  }
  function getManifest() {
    return manifest;
  }
  async function loadManifest() {
    if (manifest) {
      return manifest;
    }
    return manifestPromise ?? (manifestPromise = setManifest().finally(() => manifestPromise = void 0).then(() => manifest));
  }
  async function setManifest() {
    const { artifacts, ...rest } = await fetch(manifestUrl).then(
      (res) => res.json()
    );
    manifest = { ...rest, entryStatic: {} };
    for (const chunk of Object.values(artifacts)) {
      if (chunk.name && chunk.isEntry && chunk.src) {
        const entryStatic = {
          file: chunk.file,
          css: /* @__PURE__ */ new Set(),
          imports: /* @__PURE__ */ new Set()
        };
        const entryDeepChunks = /* @__PURE__ */ new Set();
        (artifacts[chunk.src].css ?? []).forEach((dep) => entryStatic.css.add(dep));
        (artifacts[chunk.src].imports ?? []).forEach((dep) => entryDeepChunks.add(dep));
        for (const chunkKey of entryDeepChunks) {
          const deepChunk = artifacts[chunkKey];
          if (!deepChunk) {
            continue;
          }
          entryStatic.imports.add(deepChunk.file);
          (deepChunk.css ?? []).forEach((dep) => entryStatic.css.add(dep));
          (deepChunk.imports ?? []).forEach((dep) => entryDeepChunks.add(dep));
        }
        manifest.entryStatic[chunk.name] = entryStatic;
      }
    }
  }
  async function preloadModuleDedupe(dep) {
    const statusPromise = preloadMap.modules.get(dep) ?? insertMap.modules.get(dep);
    if (statusPromise) {
      await statusPromise;
      return;
    }
    const promise = appendLink(dep, { rel: "modulepreload" }).catch((error) => {
      preloadMap.modules.delete(dep);
      throw error;
    });
    preloadMap.modules.set(dep, promise);
    await promise;
  }
  async function preloadStylesDedupe(dep) {
    const statusPromise = preloadMap.styles.get(dep) ?? insertMap.styles.get(dep);
    if (statusPromise) {
      await statusPromise;
      return;
    }
    const promise = appendLink(dep, { rel: "preload", as: "style" }).catch((error) => {
      preloadMap.styles.delete(dep);
      throw error;
    });
    preloadMap.styles.set(dep, promise);
    await promise;
  }
  async function insertStylesDedupe(dep) {
    const insertStatusPromise = insertMap.styles.get(dep);
    if (insertStatusPromise) {
      await insertStatusPromise;
      return;
    }
    const promise = appendLink(dep, { rel: "stylesheet" }).catch((error) => {
      insertMap.styles.delete(dep);
      throw error;
    });
    insertMap.styles.set(dep, promise);
    await promise;
  }
  async function appendLink(dep, props) {
    const href = joinPath(url, dep);
    const attrs = [
      ["href", href],
      ["rel", props.rel],
      ...props.as ? [["as", props.as]] : [],
      ...props.crossorigin ? [["crossorigin", ""]] : []
    ];
    const selector = attrs.map(([key, value]) => `[${key}${value ? `="${value}"` : ""}]`).join("");
    if (document.querySelector(`link${selector}`)) {
      return;
    }
    const link = document.createElement("link");
    for (const [key, value] of attrs) {
      link.setAttribute(key, value);
    }
    document.head.appendChild(link);
    return new Promise((res, rej) => {
      link.addEventListener("load", () => res());
      link.addEventListener("error", () => {
        document.head.removeChild(link);
        rej(new Error(`Unable to preload ${dep}`));
      });
    });
  }
}
export {
  useFragmentNetwork as u
};
//# sourceMappingURL=fragmentNetwork.js.map
